import { useEffect, useMemo, useState } from 'react'
import { useAccounts, useBulkRecategorize, useInfiniteTransactions, useRecategorize, useSetCleared } from './hooks'
import { useBudgetMonth } from '@/features/budget/hooks'
import { currentYearMonth } from '@/features/budget/format'
import type { CategoryGroup } from '@/features/budget/types'
import type { Transaction } from './types'
import { CategoryDropdown } from './CategoryDropdown'
import { AddTransactionDialog } from './AddTransactionDialog'
import { BulkToolbar } from './BulkToolbar'
import { SplitDialog } from './SplitDialog'
import { formatAmount, formatDate } from './format'

/**
 * Transactions MVP — list, filter, inline recategorize.
 *
 * Categories live on the current BudgetMonth doc; we use the active
 * month for the picker. Filtering happens client-side (the dataset
 * is small enough for now); a server-side filter step lands when
 * pagination does.
 *
 * Bulk select, splits, reconciliation, and pagination are explicit
 * follow-ups — see WEB-19..23 in the plan.
 */
export function TransactionsScreen({ focusTxnId = null }: { focusTxnId?: string | null }) {
  // The selected date-range chip drives a server-side `where('date','>=', floor)`
  // so the network response is already pre-trimmed. Empty floor = All time.
  type DateRange = '30d' | '90d' | '1y' | 'all'
  // Deep-linked transactions can be arbitrarily old — open all-time
  // so the auto page-walk below can always reach them.
  const [dateRange, setDateRange] = useState<DateRange>(focusTxnId ? 'all' : '90d')
  const dateFloor = useMemo(() => {
    if (dateRange === 'all') return null
    const days = dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365
    const d = new Date()
    d.setDate(d.getDate() - days)
    return d.toISOString().slice(0, 10)
  }, [dateRange])

  // Cursor-paginated; 2k rows per page. The Firestore Web SDK chokes
  // on a single 10k+ row read for YNAB-imported budgets (transport
  // limits), so we walk pages and merge in memory.
  const txQuery = useInfiniteTransactions(2000, dateFloor)
  const accountsQuery = useAccounts()
  const ym = currentYearMonth()
  const monthQuery = useBudgetMonth(ym.year, ym.month)
  const recategorize = useRecategorize()
  const bulkRecategorize = useBulkRecategorize()
  const setCleared = useSetCleared()

  function toggleCleared(t: Transaction) {
    if (t.cleared === 'reconciled') {
      if (!window.confirm('This transaction is reconciled (locked). Unlock it back to cleared?'))
        return
      setCleared.mutate({ transactionId: t.id, cleared: 'cleared' })
      return
    }
    setCleared.mutate({
      transactionId: t.id,
      cleared: t.cleared === 'cleared' ? 'uncleared' : 'cleared',
    })
  }

  const [accountFilter, setAccountFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'uncategorized' | 'cleared' | 'uncleared'>(
    'all',
  )
  const [search, setSearch] = useState('')
  // Render-side window. Even after server-side pagination, a heavily
  // filtered or "all-time" view can still produce thousands of matched
  // rows; painting them all at once freezes the tab.
  const PAGE_SIZE = 500
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [addOpen, setAddOpen] = useState(false)
  const [splitTarget, setSplitTarget] = useState<Transaction | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [anchorId, setAnchorId] = useState<string | null>(null)

  const groups = monthQuery.data?.categoryGroups ?? []
  const accountById = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of accountsQuery.data ?? []) m.set(a.id, a.name)
    return m
  }, [accountsQuery.data])

  // Flatten all loaded pages into a single newest-first array.
  const allRows = useMemo<Transaction[]>(
    () => txQuery.data?.pages.flatMap((p) => p.rows) ?? [],
    [txQuery.data],
  )

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    return allRows.filter((t) => {
      // Date floor is enforced server-side now; no client-side prune.
      if (accountFilter && t.accountId !== accountFilter) return false
      if (statusFilter === 'uncategorized' && t.categoryId) return false
      if (statusFilter === 'cleared' && t.cleared !== 'cleared' && t.cleared !== 'reconciled') return false
      if (statusFilter === 'uncleared' && t.cleared === 'cleared') return false
      if (statusFilter === 'uncleared' && t.cleared === 'reconciled') return false
      if (s) {
        const haystack = [t.payeeName ?? '', t.memo ?? '', t.description ?? '', t.originalCategory ?? '']
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(s)) return false
      }
      return true
    })
  }, [allRows, accountFilter, statusFilter, search])

  // If a search would benefit from loading more pages, surface that
  // to the user. When the search box is non-empty and there's a next
  // page available, also bias the "Load older" CTA more strongly.
  const earliestLoaded = allRows.length > 0 ? allRows[allRows.length - 1].date : null

  // Reset the visible window when filters change so users always see
  // the top of the filtered list.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [accountFilter, statusFilter, search, dateRange])

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount])

  // Deep-link focus (?txn=<id>): walk older pages until the row is
  // loaded, widen the render window to include it, then scroll it
  // into view. Bounded by the dataset — each step loads one page.
  const focusLoaded = !focusTxnId || allRows.some((t) => t.id === focusTxnId)
  useEffect(() => {
    if (focusLoaded || !txQuery.hasNextPage || txQuery.isFetchingNextPage) return
    void txQuery.fetchNextPage()
  }, [focusLoaded, txQuery])
  useEffect(() => {
    if (!focusTxnId) return
    const idx = filtered.findIndex((t) => t.id === focusTxnId)
    if (idx >= 0 && idx >= visibleCount) setVisibleCount(idx + 50)
  }, [focusTxnId, filtered, visibleCount])
  useEffect(() => {
    if (!focusTxnId) return
    const el = document.getElementById(`txn-${focusTxnId}`)
    el?.scrollIntoView({ block: 'center' })
  }, [focusTxnId, visible])

  // Payee autocomplete suggestions, frequency-sorted from the loaded
  // set so the dropdown leads with the names you actually have. Cap
  // keeps the DOM small on large histories.
  const payeeSuggestions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const t of allRows) {
      if (t.payeeName) counts.set(t.payeeName, (counts.get(t.payeeName) ?? 0) + 1)
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 200)
      .map(([name]) => name)
  }, [allRows])

  function toggleOne(id: string, shiftKey: boolean) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (shiftKey && anchorId) {
        // Range select on shift-click: select all rows between the
        // anchor and the just-clicked row, inclusive, using the
        // current filtered order.
        const a = filtered.findIndex((t) => t.id === anchorId)
        const b = filtered.findIndex((t) => t.id === id)
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a]
          for (let i = lo; i <= hi; i++) next.add(filtered[i].id)
          return next
        }
      }
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setAnchorId(id)
  }

  function selectAllVisible() {
    setSelected(new Set(filtered.map((t) => t.id)))
  }
  function clearSelection() {
    setSelected(new Set())
    setAnchorId(null)
  }

  function doBulkCategorize(categoryId: string | null) {
    if (selected.size === 0) return
    bulkRecategorize.mutate(
      { ids: Array.from(selected), categoryId },
      { onSuccess: () => clearSelection() },
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Transactions</h1>
        <div className="flex items-center gap-3">
          <div className="text-sm text-zinc-500">
            {filtered.length.toLocaleString()} of {allRows.length.toLocaleString()} loaded
            {txQuery.hasNextPage ? ' · more available' : ''}
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            disabled={(accountsQuery.data?.length ?? 0) === 0}
            className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            + New
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRange)}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
          aria-label="Date range"
        >
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
          <option value="all">All time</option>
        </select>

        <select
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
          aria-label="Filter by account"
        >
          <option value="">All accounts</option>
          {(accountsQuery.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
          aria-label="Filter by status"
        >
          <option value="all">All transactions</option>
          <option value="uncategorized">Uncategorized only</option>
          <option value="cleared">Cleared</option>
          <option value="uncleared">Uncleared</option>
        </select>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search payee / memo / description"
          list="tx-payee-suggestions"
          autoComplete="off"
          className="min-w-[220px] flex-1 rounded-md border border-zinc-300 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
          aria-label="Search transactions"
        />
        <datalist id="tx-payee-suggestions">
          {payeeSuggestions.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>

        {txQuery.hasNextPage && (
          <button
            type="button"
            onClick={() => txQuery.fetchNextPage()}
            disabled={txQuery.isFetchingNextPage}
            title={earliestLoaded ? `Older than ${earliestLoaded}` : ''}
            className="rounded-md border border-zinc-300 px-2 py-1 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            {txQuery.isFetchingNextPage ? 'Loading…' : 'Load older'}
          </button>
        )}
      </div>

      {txQuery.isPending && (
        <div className="rounded-xl border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
          Loading…
        </div>
      )}

      {txQuery.isError && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          Couldn&apos;t load transactions. {String((txQuery.error as Error)?.message ?? '')}
        </div>
      )}

      {txQuery.isSuccess && filtered.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700">
          {allRows.length === 0 ? (
            'No transactions yet. Link a bank from Accounts to pull them in.'
          ) : txQuery.hasNextPage ? (
            <>
              No matches in the loaded set.{' '}
              <button
                type="button"
                onClick={() => txQuery.fetchNextPage()}
                className="font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                Load older to keep searching
              </button>
              .
            </>
          ) : (
            'No matches for these filters.'
          )}
        </div>
      )}

      {selected.size > 0 && (
        <BulkToolbar
          selectedCount={selected.size}
          totalCount={filtered.length}
          groups={groups}
          onClear={clearSelection}
          onCategorize={doBulkCategorize}
          busy={bulkRecategorize.isPending}
        />
      )}

      <AddTransactionDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        accounts={accountsQuery.data ?? []}
        groups={groups}
        defaultAccountId={accountFilter || accountsQuery.data?.[0]?.id}
      />

      {splitTarget && (
        <SplitDialog
          open
          onClose={() => setSplitTarget(null)}
          transaction={splitTarget}
          groups={groups}
        />
      )}

      {filtered.length > 0 && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800">
          <div
            role="grid"
            aria-rowcount={filtered.length + 1}
            aria-colcount={7}
            className="divide-y divide-zinc-200 dark:divide-zinc-800"
          >
            <div
              role="row"
              className="grid grid-cols-[32px_100px_140px_1fr_220px_120px_80px] gap-2 bg-zinc-100 px-3 py-2 text-xs font-medium uppercase tracking-wide text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
            >
              <div role="columnheader" className="flex items-center">
                <input
                  type="checkbox"
                  aria-label="Select all visible"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  ref={(el) => {
                    if (el)
                      el.indeterminate = selected.size > 0 && selected.size < filtered.length
                  }}
                  onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                />
              </div>
              <div role="columnheader">Date</div>
              <div role="columnheader">Account</div>
              <div role="columnheader">Payee / Memo</div>
              <div role="columnheader">Category</div>
              <div role="columnheader" className="text-right">
                Amount
              </div>
              <div role="columnheader">Status</div>
            </div>
            {visible.map((t) => (
              <Row
                key={t.id}
                transaction={t}
                accountName={accountById.get(t.accountId) ?? 'Unknown'}
                groups={groups}
                highlighted={t.id === focusTxnId}
                selected={selected.has(t.id)}
                onToggle={(shiftKey) => toggleOne(t.id, shiftKey)}
                onRecategorize={(categoryId) =>
                  recategorize.mutate({ transactionId: t.id, categoryId })
                }
                onSplit={() => setSplitTarget(t)}
                onToggleCleared={() => toggleCleared(t)}
              />
            ))}
          </div>
          {filtered.length > visible.length && (
            <div className="flex items-center justify-between gap-3 border-t border-zinc-200 px-3 py-2 text-sm dark:border-zinc-800">
              <span className="text-zinc-500">
                Showing {visible.length.toLocaleString()} of {filtered.length.toLocaleString()}
              </span>
              <button
                type="button"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="rounded-md border border-zinc-300 px-3 py-1 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
              >
                Load {Math.min(PAGE_SIZE, filtered.length - visible.length).toLocaleString()} more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface RowProps {
  transaction: Transaction
  accountName: string
  groups: CategoryGroup[]
  selected: boolean
  highlighted?: boolean
  onToggle: (shiftKey: boolean) => void
  onRecategorize: (categoryId: string | null) => void
  onSplit: () => void
  onToggleCleared: () => void
}

function Row({
  transaction,
  accountName,
  onRecategorize,
  onSplit,
  groups,
  selected,
  highlighted = false,
  onToggle,
  onToggleCleared,
}: RowProps) {
  const splitCount = transaction.splits.length
  const isInflow = transaction.amount > 0
  return (
    <div
      role="row"
      id={`txn-${transaction.id}`}
      aria-selected={selected}
      className={`grid grid-cols-[32px_100px_140px_1fr_220px_120px_80px] items-center gap-2 px-3 py-2 text-sm ${
        selected ? 'bg-blue-50 dark:bg-blue-950/40' : ''
      } ${highlighted ? 'ring-2 ring-inset ring-blue-500 bg-blue-50/60 dark:bg-blue-950/30' : ''}`}
    >
      <div role="gridcell" className="flex items-center">
        <input
          type="checkbox"
          aria-label={`Select transaction ${transaction.payeeName ?? transaction.description ?? transaction.memo ?? transaction.id}`}
          checked={selected}
          onClick={(e) => {
            // <input> doesn't expose shiftKey via onChange — onClick
            // does, and React still fires onChange afterwards which
            // would flip the state again. Intercept here, call our
            // handler, and let the parent govern the checked state.
            e.preventDefault()
            onToggle(e.shiftKey)
          }}
          onChange={() => {
            /* state owned by parent */
          }}
        />
      </div>
      <div role="gridcell" className="tabular-nums text-zinc-600 dark:text-zinc-300">
        {formatDate(transaction.date)}
      </div>
      <div role="gridcell" className="truncate text-zinc-700 dark:text-zinc-200">
        {accountName}
      </div>
      <div role="gridcell" className="truncate" title={transaction.memo ?? transaction.description ?? ''}>
        <div className="truncate font-medium">
          {transaction.payeeName ?? transaction.description ?? transaction.memo ?? '—'}
        </div>
        {(transaction.memo && transaction.payeeName) && (
          <div className="truncate text-xs text-zinc-500">{transaction.memo}</div>
        )}
        {transaction.originalCategory && (
          <div className="truncate text-xs text-zinc-500">{transaction.originalCategory}</div>
        )}
      </div>
      <div role="gridcell" className="flex items-center gap-1">
        {splitCount > 0 ? (
          <button
            type="button"
            onClick={onSplit}
            className="w-full truncate rounded px-2 py-1 text-left text-sm font-medium text-blue-600 hover:bg-zinc-100 dark:text-blue-400 dark:hover:bg-zinc-800"
            title={`Edit ${splitCount} splits`}
          >
            Split ({splitCount})
          </button>
        ) : (
          <div className="flex-1">
            <CategoryDropdown
              groups={groups}
              value={transaction.categoryId ?? null}
              onChange={onRecategorize}
              highlightEmpty
            />
          </div>
        )}
        <button
          type="button"
          onClick={onSplit}
          aria-label={splitCount > 0 ? 'Edit splits' : 'Split transaction'}
          title={splitCount > 0 ? 'Edit splits' : 'Split transaction'}
          className="rounded px-1 text-xs text-zinc-400 hover:bg-zinc-100 hover:text-blue-600 dark:hover:bg-zinc-800"
        >
          ⊞
        </button>
      </div>
      <div
        role="gridcell"
        className={`text-right tabular-nums ${isInflow ? 'text-funded' : ''}`}
      >
        {formatAmount(transaction.amount)}
      </div>
      <div role="gridcell">
        <button
          type="button"
          onClick={onToggleCleared}
          aria-label={
            transaction.cleared === 'reconciled'
              ? 'Reconciled (locked) — click to unlock'
              : transaction.cleared === 'cleared'
                ? 'Cleared — click to mark uncleared'
                : 'Uncleared — click to mark cleared'
          }
          title={
            transaction.cleared === 'reconciled'
              ? 'Reconciled (locked)'
              : transaction.cleared === 'cleared'
                ? 'Cleared'
                : 'Uncleared'
          }
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
            transaction.cleared === 'reconciled'
              ? 'bg-funded-subtle text-funded'
              : transaction.cleared === 'cleared'
                ? 'text-funded hover:bg-funded-subtle'
                : 'text-zinc-400 hover:bg-zinc-100 hover:text-funded dark:hover:bg-zinc-800'
          }`}
        >
          {transaction.cleared === 'reconciled' ? 'R' : 'C'}
        </button>
      </div>
    </div>
  )
}
